import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Icon, Button } from 'antd';
import moment from 'moment';
import 'moment/locale/pt-br';
import styled, { css, keyframes } from 'styled-components';

import { fetchConferenceSummary } from '~/_domains/conference/helpers';

const sirenPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(245, 34, 45, 0.45); }
  70% { box-shadow: 0 0 0 12px rgba(245, 34, 45, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 34, 45, 0); }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const IconCircle = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;

  ${(props) => props.tone === 'danger' && css`
    background: #fff1f0;
    color: #f5222d;
    animation: ${sirenPulse} 1.6s infinite;
  `}

  ${(props) => props.tone === 'warning' && css`
    background: #fffbe6;
    color: #faad14;
  `}

  ${(props) => props.tone === 'ok' && css`
    background: #f6ffed;
    color: #52c41a;
  `}
`;

const Main = styled.div`
  flex: 1;
  min-width: 260px;
`;

const Headline = styled.div`
  font-size: 17px;
  font-weight: 600;
  line-height: 1.3;
  color: ${(props) => props.color || '#262626'};
`;

const Sub = styled.div`
  font-size: 13px;
  color: #8c8c8c;
  margin: 4px 0 10px;

  b {
    color: #595959;
  }
`;

const Track = styled.div`
  height: 6px;
  max-width: 420px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  border-radius: 3px;
  width: ${(props) => props.percent}%;
  background: ${(props) => props.color};
  transition: width 0.4s ease;
`;

const ProgressLabel = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 4px;
`;

export const ConferenceWidget = () => {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let isMounted = true;
    fetchConferenceSummary().then((data) => {
      if (isMounted) setSummary(data);
    });
    return () => { isMounted = false; };
  }, []);

  const sirenPending = summary ? summary.sirenPending : 0;
  const sirenTasks = summary ? summary.sirenTasks : 0;
  const pending = summary ? summary.pending : 0;
  const late = summary ? summary.late : 0;
  const tasksCount = summary ? summary.tasks.length : 0;

  let notifiedTotal = 0;
  let notifiableTotal = 0;
  if (summary) {
    summary.tasks.forEach((task) => {
      notifiedTotal += task.notified;
      notifiableTotal += task.notifiable;
    });
  }
  const percent = notifiableTotal > 0 ? Math.round((notifiedTotal / notifiableTotal) * 100) : 100;

  let tone = 'ok';
  let icon = 'check-circle';
  let headline = 'Tudo em dia com os avisos de guias';
  let headlineColor = '#52c41a';

  if (sirenPending > 0) {
    tone = 'danger';
    icon = 'alert';
    headline = `${sirenPending} ${sirenPending === 1 ? 'cliente precisa' : 'clientes precisam'} ser avisados agora`;
    headlineColor = '#f5222d';
  } else if (pending > 0) {
    tone = 'warning';
    icon = 'bell';
    headline = `${pending} avisos de guias pendentes neste mês`;
    headlineColor = '#d48806';
  }

  const subParts = [];
  if (sirenPending > 0) {
    subParts.push(`vencimento em até 1 dia em ${sirenTasks} ${sirenTasks === 1 ? 'tarefa' : 'tarefas'}`);
  }
  subParts.push(`${tasksCount} ${tasksCount === 1 ? 'tarefa está' : 'tarefas estão'} na Conferência em ${moment().format('MMMM')}`);
  if (late > 0) {
    subParts.push(`${late} ${late === 1 ? 'aviso feito' : 'avisos feitos'} fora do prazo`);
  }

  return (
    <Card loading={!summary} bodyStyle={{ padding: '20px 24px' }}>
      {summary && (
        <Wrapper>
          <IconCircle tone={tone}>
            <Icon type={icon} theme={tone === 'ok' ? 'filled' : 'outlined'} />
          </IconCircle>
          <Main>
            <Headline color={headlineColor}>{headline}</Headline>
            <Sub>
              Conferência de guias · {subParts.join(' · ')}
            </Sub>
            <Track>
              <Fill
                percent={percent}
                color={tone === 'danger' ? '#f5222d' : tone === 'warning' ? '#faad14' : '#52c41a'}
              />
            </Track>
            <ProgressLabel>
              {notifiedTotal} de {notifiableTotal} clientes avisados ({percent}%)
            </ProgressLabel>
          </Main>
          <Link to="/conferencia">
            <Button type={tone === 'danger' ? 'danger' : 'primary'} icon="alert">
              Abrir Conferência
            </Button>
          </Link>
        </Wrapper>
      )}
    </Card>
  );
};
